/**
 * External dependencies
 */
import { __ } from '@wordpress/i18n';
import { InspectorControls, useBlockProps } from '@wordpress/block-editor';
import {
	PanelBody,
	SelectControl,
	ToggleControl,
	TextControl,
	__experimentalToggleGroupControl as ToggleGroupControl, // eslint-disable-line @wordpress/no-unsafe-wp-apis
	__experimentalToggleGroupControlOption as ToggleGroupControlOption, // eslint-disable-line @wordpress/no-unsafe-wp-apis
} from '@wordpress/components';

const Sun = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-sun-icon lucide-sun"><circle cx="12" cy="12" r="4" /><path d="M12 2v2" /><path d="M12 20v2" /><path d="m4.93 4.93 1.41 1.41" /><path d="m17.66 17.66 1.41 1.41" /><path d="M2 12h2" /><path d="M20 12h2" /><path d="m6.34 17.66-1.41 1.41" /><path d="m19.07 4.93-1.41 1.41" /></svg>;
const Moon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-moon-icon lucide-moon"><path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" /></svg>;
const Lightbulb = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-lightbulb-icon lucide-lightbulb"><path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5" /><path d="M9 18h6" /><path d="M10 22h4" /></svg>;
const LightbulbOff = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-lightbulb-off-icon lucide-lightbulb-off"><path d="M16.8 11.2c.8-.9 1.2-2 1.2-3.2a6 6 0 0 0-9.3-5" /><path d="m2 2 20 20" /><path d="M6.3 6.3a4.67 4.67 0 0 0 1.2 5.2c.7.7 1.3 1.5 1.5 2.5" /><path d="M9 18h6" /><path d="M10 22h4" /></svg>;
const SunMoon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-sun-moon-icon lucide-sun-moon"><path d="M12 2v2" /><path d="M14.837 16.385a6 6 0 1 1-7.223-7.222c.624-.147.97.66.715 1.248a4 4 0 0 0 5.26 5.259c.589-.255 1.396.09 1.248.715" /><path d="M16 12a4 4 0 0 0-4-4" /><path d="m19 5-1.256 1.256" /><path d="M20 12h2" /></svg>;
const Sunrise = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-sunrise-icon lucide-sunrise"><path d="M12 2v8" /><path d="m4.93 10.93 1.41 1.41" /><path d="M2 18h2" /><path d="M20 18h2" /><path d="m19.07 10.93-1.41 1.41" /><path d="M22 22H2" /><path d="m8 6 4-4 4 4" /><path d="M16 18a4 4 0 0 0-8 0" /></svg>;
const Sunset = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-sunset-icon lucide-sunset"><path d="M12 10V2" /><path d="m4.93 10.93 1.41 1.41" /><path d="M2 18h2" /><path d="M20 18h2" /><path d="m19.07 10.93-1.41 1.41" /><path d="M22 22H2" /><path d="m16 6-4 4-4-4" /><path d="M16 18a4 4 0 0 0-8 0" /></svg>;

export default function Edit( { attributes, setAttributes } ) {
	const {
		switcherType,
		forceReload,
		systemPreferenceMode,
		lightIcon,
		darkIcon,
		iconSize,
		lightText,
		darkText,
	} = attributes;

	const blockProps = useBlockProps( {
		className: `switcher-button preview-button portal-button button-primary`,
	} );

	// Icon options with lucide components for preview.
	const iconOptions = [
		{ label: __( 'Sun', 'suredash' ), value: 'sun', component: Sun },
		{ label: __( 'Moon', 'suredash' ), value: 'moon', component: Moon },
		{ label: __( 'Lightbulb', 'suredash' ), value: 'lightbulb', component: Lightbulb },
		{ label: __( 'Lightbulb Off', 'suredash' ), value: 'lightbulboff', component: LightbulbOff },
		{ label: __( 'Sun Moon', 'suredash' ), value: 'sunmoon', component: SunMoon },
		{ label: __( 'Sunrise', 'suredash' ), value: 'sunrise', component: Sunrise },
		{ label: __( 'Sunset', 'suredash' ), value: 'sunset', component: Sunset },
	];

	// Get icon component by value.
	const getIconByValue = ( value ) => {
		const iconOption = iconOptions.find( ( option ) => option.value === value );
		return iconOption ? iconOption.component : Sun;
	};

	// Render preview of the color switcher.
	const renderPreview = () => {
		const LightIconComponent = getIconByValue( lightIcon );

		return (
			<div className="color-switcher-preview">
				<button { ...blockProps }>
					{ ( switcherType === 'icon' || switcherType === 'icon_text' ) && (
						<LightIconComponent size={ 16 } className="portal-svg-icon" />
					) }
					{ ( switcherType === 'text' || switcherType === 'icon_text' ) && (
						<span className="switcher-text">
							{ lightText }
						</span>
					) }
				</button>
			</div>
		);
	};

	return (
		<>
			<InspectorControls>
				<PanelBody title={ __( 'Switcher Settings', 'suredash' ) }>
					<SelectControl
						label={ __( 'Switcher Type', 'suredash' ) }
						value={ switcherType }
						options={ [
							{ label: __( 'Icon Only', 'suredash' ), value: 'icon' },
							{ label: __( 'Text Only', 'suredash' ), value: 'text' },
							{ label: __( 'Icon + Text', 'suredash' ), value: 'icon_text' },
						] }
						onChange={ ( value ) => setAttributes( { switcherType: value } ) }
					/>

					<ToggleControl
						label={ __( 'Force Page Reload', 'suredash' ) }
						checked={ forceReload }
						onChange={ ( value ) => setAttributes( { forceReload: value } ) }
						help={ __( 'Reload page after switching (useful for complex themes).', 'suredash' ) }
					/>

					<ToggleControl
						label={ __( 'System Preference Mode', 'suredash' ) }
						checked={ systemPreferenceMode }
						onChange={ ( value ) => setAttributes( { systemPreferenceMode: value } ) }
						help={ __( 'Prefers system mode over Default-Alternate mode.', 'suredash' ) }
					/>
				</PanelBody>

				{ ( switcherType === 'icon' || switcherType === 'icon_text' ) && (
					<PanelBody title={ __( 'Icon Settings', 'suredash' ) } initialOpen={ false }>
						<ToggleGroupControl
							__nextHasNoMarginBottom
							label={ __( 'Default Mode Icon', 'suredash' ) }
							value={ lightIcon }
							onChange={ ( value ) => setAttributes( { lightIcon: value } ) }
							isBlock
						>
							{ iconOptions.map( ( option ) => (
								<ToggleGroupControlOption
									key={ option.value }
									value={ option.value }
									label={
										<div style={ { display: 'flex', alignItems: 'center', gap: '8px' } }>
											<option.component size={ 16 } />
										</div>
									}
									aria-label={ option.label }
								/>
							) ) }
						</ToggleGroupControl>

						<ToggleGroupControl
							__nextHasNoMarginBottom
							label={ __( 'Switched Mode Icon', 'suredash' ) }
							value={ darkIcon }
							onChange={ ( value ) => setAttributes( { darkIcon: value } ) }
							isBlock
						>
							{ iconOptions.map( ( option ) => (
								<ToggleGroupControlOption
									key={ option.value }
									value={ option.value }
									label={
										<div style={ { display: 'flex', alignItems: 'center', gap: '8px' } }>
											<option.component size={ 16 } />
										</div>
									}
									aria-label={ option.label }
								/>
							) ) }
						</ToggleGroupControl>

						<ToggleGroupControl
							__nextHasNoMarginBottom
							label={ __( 'Icon Size', 'suredash' ) }
							value={ iconSize }
							onChange={ ( value ) => setAttributes( { iconSize: value } ) }
							isBlock
						>
							<ToggleGroupControlOption
								value="sm"
								label={ __( 'SM', 'suredash' ) }
								aria-label={ __( 'Small', 'suredash' ) }
							/>
							<ToggleGroupControlOption
								value="md"
								label={ __( 'MD', 'suredash' ) }
								aria-label={ __( 'Medium', 'suredash' ) }
							/>
							<ToggleGroupControlOption
								value="lg"
								label={ __( 'LG', 'suredash' ) }
								aria-label={ __( 'Large', 'suredash' ) }
							/>
							<ToggleGroupControlOption
								value="xl"
								label={ __( 'XL', 'suredash' ) }
								aria-label={ __( 'Extra Large', 'suredash' ) }
							/>
						</ToggleGroupControl>
					</PanelBody>
				) }

				{ ( switcherType === 'text' || switcherType === 'icon_text' ) && (
					<PanelBody title={ __( 'Text Settings', 'suredash' ) } initialOpen={ false }>
						<TextControl
							label={ __( 'Default Mode Text', 'suredash' ) }
							value={ lightText }
							onChange={ ( value ) => setAttributes( { lightText: value } ) }
							placeholder={ __( 'Light', 'suredash' ) }
						/>

						<TextControl
							label={ __( 'Switched Mode Text', 'suredash' ) }
							value={ darkText }
							onChange={ ( value ) => setAttributes( { darkText: value } ) }
							placeholder={ __( 'Dark', 'suredash' ) }
						/>
					</PanelBody>
				) }
			</InspectorControls>

			<div className="suredash-color-switcher-editor">
				<div className="color-switcher-editor-wrapper">
					{ renderPreview() }
				</div>
			</div>
		</>
	);
}
